import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2, Loader2, RotateCcw, X, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { InlineError } from './InlineError';
import { layoutError, loadCatalogue } from '../lib/layout';
import type { HomeLayout, WidgetCatalogueEntry, WidgetConfigProperty, WidgetSpec } from '../lib/types';

function nextId(widgets: WidgetSpec[], widget: string): string {
  const taken = new Set(widgets.map(w => w.id));
  if (!taken.has(widget)) return widget;
  let n = 2;
  while (taken.has(`${widget}-${n}`)) n++;
  return `${widget}-${n}`;
}

const inputClass = 'w-full border border-softgrey rounded-lg px-2.5 py-1.5 text-[13px] bg-white focus:outline-none focus:border-brand';

function ConfigField({ name, prop, value, onChange }: {
  name: string;
  prop: WidgetConfigProperty;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const label = prop.title ?? name;
  if (prop.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-[13px] text-graphite">
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked || undefined)} />
        {label}
      </label>
    );
  }
  let control;
  if (prop.type === 'string' && prop.enum) {
    control = (
      <select className={inputClass} value={(value as string) ?? ''} onChange={e => onChange(e.target.value || undefined)}>
        <option value="">Default</option>
        {prop.enum.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  } else if (prop.type === 'string' && prop.format === 'markdown') {
    control = (
      <textarea className={inputClass} rows={4} value={(value as string) ?? ''} onChange={e => onChange(e.target.value || undefined)} />
    );
  } else if (prop.type === 'integer') {
    control = (
      <input
        type="number" className={inputClass} min={prop.minimum} max={prop.maximum}
        value={value === undefined ? '' : String(value)}
        onChange={e => onChange(e.target.value === '' ? undefined : Number.parseInt(e.target.value, 10))}
      />
    );
  } else if (prop.type === 'array') {
    control = (
      <input
        className={inputClass} placeholder="Separate with commas"
        value={Array.isArray(value) ? value.join(', ') : ''}
        onChange={e => {
          const items = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
          onChange(items.length ? items : undefined);
        }}
      />
    );
  } else {
    control = <input className={inputClass} value={(value as string) ?? ''} onChange={e => onChange(e.target.value || undefined)} />;
  }
  return (
    <label className="block">
      <span className="block text-[12px] text-coolslate mb-1">{label}</span>
      {control}
    </label>
  );
}

// Choose what Home shows: its widgets, in order, and how each is set up.
// Saved for the whole workspace.
export default function ScreenEditor({ screen, onDone }: {
  screen: HomeLayout;
  onDone: (changed: boolean) => void;
}) {
  const { authFetch } = useAuth();
  const { refresh } = useWorkspace();
  const [catalogue, setCatalogue] = useState<WidgetCatalogueEntry[] | null>(null);
  const [widgets, setWidgets] = useState<WidgetSpec[]>(screen.widgets);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCatalogue(authFetch).then(setCatalogue).catch(err => setError(err.message));
  }, [authFetch]);

  const byId = Object.fromEntries((catalogue ?? []).map(w => [w.id, w]));

  const move = (i: number, by: number) => setWidgets(ws => {
    const next = [...ws];
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item);
    return next;
  });
  const setConfig = (i: number, key: string, value: unknown) => setWidgets(ws => ws.map((w, j) => {
    if (j !== i) return w;
    const config = { ...(w.config ?? {}) };
    if (value === undefined) delete config[key]; else config[key] = value;
    return { ...w, config };
  }));

  const run = async (request: () => Promise<Response>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await request();
      if (!res.ok) { setError(await layoutError(res, fallback)); return; }
      await refresh();
      onDone(true);
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(
    () => authFetch('/layout/home', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ widgets }),
    }),
    'Could not save this screen.',
  );
  const reset = () => {
    if (!window.confirm('Go back to the layout the workspace template gives this screen?')) return;
    run(() => authFetch('/layout/home/reset', { method: 'POST' }), 'Could not reset this screen.');
  };

  return (
    <div className="border border-brand/30 rounded-xl bg-white p-4 space-y-4">
      {error && <InlineError message={error} className="" />}

      {screen.template_changed && (
        <p className="flex items-start gap-2 text-[13px] text-coolslate bg-offwhite rounded-lg p-3">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-brand-dark" />
          The workspace template has changed this screen since it was customised. Reset to see the template's version.
        </p>
      )}

      <ol className="space-y-2">
        {widgets.map((w, i) => {
          const def = byId[w.widget];
          const props = Object.entries(def?.config.properties ?? {});
          return (
            <li key={w.id} className="border border-softgrey rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <span className="block text-[13px] font-medium text-graphite">{def?.name ?? w.widget}</span>
                  {def && <span className="block text-[12px] text-coolslate">{def.description}</span>}
                </div>
                <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-offwhite disabled:opacity-30" aria-label="Move up">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button disabled={i === widgets.length - 1} onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-offwhite disabled:opacity-30" aria-label="Move down">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button onClick={() => setWidgets(ws => ws.filter((_, j) => j !== i))} className="p-1.5 rounded hover:bg-offwhite text-state-error" aria-label="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {props.length > 0 && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {props.map(([name, prop]) => (
                    <ConfigField key={name} name={name} prop={prop} value={w.config?.[name]} onChange={v => setConfig(i, name, v)} />
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {picking ? (
        <div className="border border-softgrey rounded-lg p-3">
          <div className="flex items-center mb-2">
            <span className="flex-1 text-[13px] font-medium text-graphite">Add a widget</span>
            <button onClick={() => setPicking(false)} className="p-1 rounded hover:bg-offwhite" aria-label="Close"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {(catalogue ?? []).map(w => (
              <button
                key={w.id}
                onClick={() => { setWidgets(ws => [...ws, { id: nextId(ws, w.id), widget: w.id }]); setPicking(false); }}
                className="text-left border border-softgrey rounded-lg px-3 py-2 hover:border-brand/40"
              >
                <span className="block text-[13px] text-graphite">{w.name}</span>
                <span className="block text-[12px] text-coolslate">{w.description}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button onClick={() => setPicking(true)} disabled={!catalogue} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-dark hover:text-brand">
          <Plus className="w-4 h-4" /> Add a widget
        </button>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-softgrey">
        <button onClick={save} disabled={busy} className="navaris-btn-solid">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
        </button>
        <button onClick={() => onDone(false)} disabled={busy} className="px-4 py-2 text-sm font-medium text-graphite border border-softgrey rounded-lg hover:bg-offwhite">
          Cancel
        </button>
        <span className="flex-1" />
        {screen.customised && (
          <button onClick={reset} disabled={busy} className="inline-flex items-center gap-1.5 text-[13px] text-coolslate hover:text-graphite">
            <RotateCcw className="w-3.5 h-3.5" /> Reset to template
          </button>
        )}
      </div>
    </div>
  );
}
